import { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserRound, KeyRound, Flame, Trophy, Check, Pencil, LogOut } from "lucide-react";
import api, { formatApiErrorDetail } from "../api";
import { useAuth } from "../context/AuthContext";
import { useUnit } from "../context/UnitContext";
import { ChangePasswordModal } from "../components/ChangePasswordModal";

export default function ProfilePage() {
  const { user, updateUser, logoutUser } = useAuth();
  const { unit, dw } = useUnit();
  const [stats, setStats] = useState(null);
  const [records, setRecords] = useState([]);
  const [name, setName] = useState(user.name);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showPwModal, setShowPwModal] = useState(false);

  useEffect(() => {
    api.get("/stats").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/records").then(({ data }) => setRecords(data)).catch(() => {});
  }, []);

  const saveName = async () => {
    if (!name.trim() || name.trim() === user.name) { setEditing(false); setName(user.name); return; }
    setSaving(true);
    try {
      const { data } = await api.put("/auth/profile", { name });
      updateUser({ name: data.name });
      toast.success(`Prénom mis à jour : ${data.name}`);
      setEditing(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="pb-32" data-testid="profile-page">
      {showPwModal && <ChangePasswordModal onClose={() => setShowPwModal(false)} />}
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-black/85 border-b border-[#2E2E3A] px-4 pt-4 pb-3">
        <h1 className="font-display text-4xl text-white leading-none flex items-center gap-2">
          <UserRound size={28} className="text-[#FF3B30]" /> PROFIL
        </h1>
        <p className="text-[#B0B0B4] text-xs mt-0.5">{user.email}</p>
      </header>

      <div className="px-4 mt-4 space-y-4">
        <div className="bg-[#14141A] border border-[#2E2E3A] p-4" data-testid="profile-name-card">
          <p className="text-[10px] uppercase tracking-widest text-[#B0B0B4] mb-2">Prénom</p>
          {editing ? (
            <div className="flex gap-2">
              <input data-testid="profile-name-input" className="auth-input flex-1" value={name} maxLength={40}
                onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && saveName()} autoFocus />
              <button data-testid="profile-name-save-button" onClick={saveName} disabled={saving}
                className="h-11 px-4 bg-[#FF3B30] text-white flex items-center gap-1.5 text-sm font-semibold hover:bg-[#e0322a] transition-colors disabled:opacity-40">
                <Check size={15} /> OK
              </button>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <p className="text-white text-xl font-semibold">{user.name}</p>
              <button data-testid="profile-name-edit-button" onClick={() => setEditing(true)}
                className="text-[#B0B0B4] hover:text-white p-2 transition-colors" aria-label="Modifier le prénom">
                <Pencil size={16} />
              </button>
            </div>
          )}
        </div>

        {stats && (
          <>
            <div className="bg-[#14141A] border border-[#FF3B30]/30 p-4 flex items-center gap-3" data-testid="profile-streak-card">
              <Flame size={28} className={stats.streak_weeks > 0 ? "text-[#FF3B30]" : "text-[#555]"} />
              <div>
                <p className="font-display text-3xl text-white leading-none">{stats.streak_weeks} SEMAINE{stats.streak_weeks > 1 ? "S" : ""}</p>
                <p className="text-[#B0B0B4] text-xs">{stats.streak_weeks > 0 ? "de streak — continue, la flamme est vivante 🔥" : "Logge une séance cette semaine pour allumer ta flamme"}</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-px bg-white/10 border border-[#2E2E3A]" data-testid="profile-stats-grid">
              {[["Séances", stats.nb_seances], ["Séries", stats.nb_series],
                [`Volume total (${unit})`, Math.round(dw(stats.volume_total)).toLocaleString("fr-CA")], ["Records battus", stats.nb_prs]].map(([l, v]) => (
                <div key={l} className="bg-[#14141A] p-4">
                  <p className="font-display text-3xl text-white">{v}</p>
                  <p className="text-[#B0B0B4] text-[10px] uppercase tracking-wider">{l}</p>
                </div>
              ))}
            </div>
          </>
        )}

        <div className="bg-[#14141A] border border-[#2E2E3A]" data-testid="profile-records-card">
          <p className="px-4 pt-3 pb-1 font-display text-xl text-white flex items-center gap-2">
            MES RECORDS <Trophy size={16} className="text-amber-400" />
          </p>
          <div className="divide-y divide-white/5">
            {records.length === 0 && <p className="px-4 py-3 text-[#B0B0B4] text-sm">Aucune série loggée pour l'instant.</p>}
            {records.map((r) => (
              <div key={r.exercice} data-testid={`record-${r.exercice}`} className="px-4 py-2">
                <div className="flex items-center justify-between">
                  <p className="text-white text-sm font-medium truncate">{r.exercice}</p>
                  <span className="text-[#B0B0B4] text-[10px] uppercase shrink-0 ml-2">{r.muscle}</span>
                </div>
                <p className="text-[#ccc] text-xs font-mono mt-0.5">
                  {r.poids_max > 0 ? `${dw(r.poids_max)} ${unit} max` : "Poids du corps"}
                  <span className="text-[#555]"> · </span>{r.reps_max} reps max
                  <span className="text-[#555]"> · </span>{Math.round(dw(r.volume_max))} {unit} vol. max
                </p>
              </div>
            ))}
          </div>
        </div>

        <button data-testid="profile-change-password-button" onClick={() => setShowPwModal(true)}
          className="w-full h-11 border border-white/15 text-[#ccc] text-sm font-semibold flex items-center justify-center gap-2 hover:border-[#FF3B30] hover:text-white transition-colors">
          <KeyRound size={15} /> Changer mon mot de passe
        </button>
        <button data-testid="profile-logout-button" onClick={logoutUser}
          className="w-full h-11 border border-[#FF3B30]/40 text-[#FF3B30] text-sm font-semibold flex items-center justify-center gap-2 hover:bg-[#FF3B30]/10 transition-colors">
          <LogOut size={15} /> Se déconnecter
        </button>
      </div>
    </div>
  );
}
